import { Box } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { addMilliseconds, endOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteTenantAvailableFloors } from 'src/api/hooks/use-infinite-tenant-available-floors';
import { InputField } from 'src/components/atoms/input-field';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import { MoreFilters } from 'src/components/organisms/more-filters';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { Anchor } from 'src/types/anchor';
import { HandleChangeFilterValue } from 'src/types/handle-change-filter-value';
import { FilterGroup as IFilterGroup } from 'src/types/interfaces/filter-group';
import { Filters } from 'src/types/interfaces/filters';
import { bookingPresenceFilterValues } from '../../constants/booking-presence/booking-presence-filter-values';
import { bookingPresenceTranslations } from '../../constants/booking-presence/booking-presence-translations';
import { BookingPresence } from '../../types/booking-presence';

interface Props {
  anchor: Anchor;
  filters: Filters;
  handleChangeFilterValue: HandleChangeFilterValue;
  resetFilters: () => void;
}

export const BookingsMoreFilters = ({
  handleChangeFilterValue,
  filters,
  resetFilters,
  anchor,
}: Props) => {
  const tenantAvailableFloorsQuery = useInfiniteTenantAvailableFloors(10);

  const floors =
    tenantAvailableFloorsQuery.data?.pages[0]?.hits?.[0]?.floors ?? [];

  const { t } = useTranslation(['bookings', 'spaces', 'common']);

  const [startDateRange, setStartDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: filters.from
      ? new Date(Number((filters.from.value as string[])[0]))
      : null,
    to: filters.from
      ? new Date(Number((filters.from.value as string[])[1]))
      : null,
  });

  const [endDateRange, setEndDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: filters.to
      ? new Date(Number((filters.to.value as string[])[0]))
      : null,
    to: filters.to ? new Date(Number((filters.to.value as string[])[1])) : null,
  });

  useEffect(() => {
    if (!startDateRange.from || !startDateRange.to) {
      return;
    }

    handleChangeFilterValue(
      'from',
      [
        startDateRange.from.getTime().toString(),
        startDateRange.to.getTime().toString(),
      ],
      'timerange',
    );
  }, [startDateRange.from, startDateRange.to]);

  useEffect(() => {
    if (!endDateRange.from || !endDateRange.to) {
      return;
    }

    handleChangeFilterValue(
      'to',
      [
        endDateRange.from.getTime().toString(),
        endDateRange.to.getTime().toString(),
      ],
      'timerange',
    );
  }, [endDateRange.from, endDateRange.to]);

  const handleResetFilters = () => {
    resetFilters();
    setStartDateRange({ from: null, to: null });
    setEndDateRange({ from: null, to: null });
  };

  const filterGroups: IFilterGroup<BookingPresence | SpaceType | string>[] = [
    {
      id: 1,
      type: 'spaceType',
      label: t('bookings:common.spaceType'),
      singleOption: false,
      items: Object.entries(spaceTypesTranslations).map(([key, value]) => ({
        id: key,
        label: t(value),
        value: key,
      })),
    },
    {
      id: 2,
      type: 'spaceFloor',
      label: t('bookings:common.spaceFloor'),
      singleOption: false,
      items: floors.map(floor => ({
        id: floor.toString(),
        label: floor.toString(),
        value: floor.toString(),
      })),
      bottomSlot: (
        <LoadingButton
          fullWidth
          variant="outlined"
          onClick={() => tenantAvailableFloorsQuery.fetchNextPage()}
          loading={tenantAvailableFloorsQuery.isFetchingNextPage}
          disabled={!tenantAvailableFloorsQuery.hasNextPage}>
          {t('common:button.loadMore')}
        </LoadingButton>
      ),
    },
    {
      id: 3,
      type: 'from',
      label: t('bookings:common.start'),
      singleOption: false,
      filterOperator: 'timerange',
      items: [],
      bottomSlot: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MobileDatePicker
              value={startDateRange.from}
              format="dd/MM/yyyy"
              onChange={date => {
                setStartDateRange(prev => ({ ...prev, from: date }));
              }}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    required
                    label={t('bookings:common.from')}
                    fullWidth
                  />
                ),
              }}
            />

            <MobileDatePicker
              value={startDateRange.to}
              format="dd/MM/yyyy"
              onChange={date => {
                setStartDateRange(prev => ({
                  ...prev,
                  to: date ? addMilliseconds(endOfDay(date), 1) : date,
                }));
              }}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    required
                    label={t('bookings:common.to')}
                    fullWidth
                  />
                ),
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      id: 4,
      type: 'to',
      label: t('bookings:common.end'),
      singleOption: false,
      items: [],
      bottomSlot: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MobileDatePicker
              value={endDateRange.from}
              format="dd/MM/yyyy"
              onChange={date => {
                setEndDateRange(prev => ({ ...prev, from: date }));
              }}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    required
                    label={t('bookings:common.from')}
                    fullWidth
                  />
                ),
              }}
            />

            <MobileDatePicker
              value={endDateRange.to}
              format="dd/MM/yyyy"
              onChange={date => {
                setEndDateRange(prev => ({
                  ...prev,
                  to: date ? addMilliseconds(endOfDay(date), 1) : date,
                }));
              }}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    required
                    label={t('bookings:common.to')}
                    fullWidth
                  />
                ),
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      id: 5,
      type: 'presence',
      label: t('bookings:common.presence.title'),
      singleOption: true,
      filterOperator: 'custom',
      items: Object.entries(bookingPresenceTranslations).map(
        ([key, value]) => ({
          id: key,
          label: t(value),
          value: bookingPresenceFilterValues[key as BookingPresence],
        }),
      ),
    },
  ];

  return (
    <MoreFilters
      anchor={anchor}
      filters={filters}
      handleChangeFilterValue={handleChangeFilterValue}
      resetFilters={handleResetFilters}
      filterGroups={filterGroups}
    />
  );
};
